'use client';

import { useState, useEffect, useCallback } from 'react';
import { getInstruments } from '../instruments/downloadInstruments';

export interface GrowwInstrument {
  exchange: string;
  exchange_token: string | number;
  trading_symbol: string;
  groww_symbol: string;
  name: string;
  instrument_type: string;
  segment: string;
  series?: string;
  isin?: string;
  underlying_symbol?: string;
  underlying_exchange_token?: string | number;
  lot_size?: number;
  expiry_date?: string;
  strike_price?: number;
  tick_size?: number;
  freeze_quantity?: number;
  is_reserved?: boolean | number;
  buy_allowed?: boolean | number;
  sell_allowed?: boolean | number;
  feed_key?: string;
}

/**
 * Custom hook to fetch and manage Groww instruments data
 */
export function useGrowwInstruments(initialSegment: string = 'CASH') {
  const [instruments, setInstruments] = useState<GrowwInstrument[]>([]);
  const [cashSegment, setCashSegment] = useState<GrowwInstrument[]>([]);
  const [fnoSegment, setFnoSegment] = useState<GrowwInstrument[]>([]);
  const [activeSegment, setActiveSegment] = useState(initialSegment);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [segmentsLoaded, setSegmentsLoaded] = useState<{[key: string]: boolean}>({});

  // Fetch instruments for a specific segment
  const fetchSegmentInstruments = useCallback(async (segment: string, search?: string) => {
    try {
      setIsLoading(true);
      console.log(`Fetching ${segment} instruments...`);
      const data = await getInstruments(segment, search);
      
      // Filter for relevant instruments based on segment
      let filteredData = [...data];
      if (segment === 'CASH') {
        // Only include EQ and BE series for CASH segment
        filteredData = data.filter(instr => 
          instr.segment === 'CASH' && 
          (instr.series === 'EQ' || instr.series === 'BE')
        );
        setCashSegment(filteredData);
      } else if (segment === 'FNO') {
        setFnoSegment(filteredData);
      }
      
      // Set the current view based on active segment
      if (segment === activeSegment) {
        setInstruments(filteredData);
      }
      
      // Mark segment as loaded
      setSegmentsLoaded(prev => ({...prev, [segment]: true}));
      
      return filteredData;
    } catch (err: any) {
      console.error(`Error loading ${segment} instruments:`, err);
      setError(err.message || `Error loading ${segment} instruments`);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [activeSegment]);

  // Handle segment change
  const changeSegment = useCallback((segment: string) => {
    console.log(`Changing segment to ${segment}`);
    setActiveSegment(segment);
    
    // If we already have data for this segment, use it
    if (segment === 'CASH' && cashSegment.length > 0) {
      console.log(`Using cached CASH segment data: ${cashSegment.length} instruments`);
      setInstruments(cashSegment);
      return;
    } else if (segment === 'FNO' && fnoSegment.length > 0) {
      console.log(`Using cached FNO segment data: ${fnoSegment.length} instruments`);
      setInstruments(fnoSegment);
      return;
    }
    
    // Otherwise fetch data for this segment
    console.log(`No cached data for ${segment}, fetching...`);
    fetchSegmentInstruments(segment);
  }, [cashSegment, fnoSegment, fetchSegmentInstruments]);

  // Handle search
  const handleSearch = useCallback(async (term: string) => {
    setSearchTerm(term);
    if (term.length >= 2) {
      // Only search if we have at least 2 characters
      const results = await fetchSegmentInstruments(activeSegment, term);
      setInstruments(results);
    } else if (term.length === 0) {
      // Reset to full segment data when search is cleared
      setInstruments(activeSegment === 'CASH' ? cashSegment : fnoSegment);
    }
  }, [activeSegment, cashSegment, fnoSegment, fetchSegmentInstruments]);

  // We no longer load any segment data on mount
  // This is now done explicitly via changeSegment

  return {
    instruments,
    cashSegment,
    fnoSegment,
    activeSegment,
    isLoading,
    error,
    searchTerm,
    changeSegment,
    handleSearch,
    segmentsLoaded
  };
}